import React, { useState } from "react";


// Reusable Card Component
export const Mo3tiCard = ({ 
  title, 
  icon: Icon, 
  children, 
  theme, 
  action,
  className = "",
  ...props 
}) => {
  const cardStyles = {
    background: theme.colors.cardBackground,
    backdropFilter: "blur(25px)",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "20px",
    padding: "24px",
    boxShadow: theme.shadows.card,
    marginBottom: "24px",
    transition: "all 0.3s ease"
  };

  const headerStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: title ? "24px" : "0"
  };

  const titleContainerStyles = {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  };

  const titleStyles = {
    color: theme.colors.textPrimary,
    fontSize: theme.fonts.sizes.xl,
    fontWeight: theme.fonts.weights.semibold,
    margin: 0,
    textShadow: theme.isDarkMode ? "0 1px 2px rgba(0,0,0,0.3)" : "none"
  };

  const iconStyles = {
    color: theme.colors.primary,
    flexShrink: 0
  };

  return (
    <div style={cardStyles} className={`mo3ti-card ${className}`} {...props}>
      {(title || Icon || action) && (
        <div style={headerStyles}>
          <div style={titleContainerStyles}>
            {Icon && <Icon size={24} style={iconStyles} />}
            {title && <h3 style={titleStyles}>{title}</h3>}
          </div>
          {action && action}
        </div>
      )}
      {children}
    </div>
  );
};

// Reusable Stat Card Component
export const Mo3tiStatCard = ({ 
  icon: Icon, 
  value, 
  label, 
  color, 
  trend, 
  subtitle,
  theme,
  onClick,
  ...props 
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardStyles = {
    background: theme.colors.cardBackground,
    backdropFilter: "blur(25px)",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "16px",
    padding: "24px",
    textAlign: "center",
    transition: "all 0.3s ease",
    cursor: onClick ? "pointer" : "default",
    position: "relative",
    overflow: "hidden",
    transform: isHovered ? "translateY(-4px)" : "translateY(0)",
    boxShadow: isHovered ? theme.shadows.card : "0 2px 8px rgba(0,0,0,0.1)"
  };

  const iconContainerStyles = {
    width: "60px",
    height: "60px",
    background: `${color}20`,
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    color: color,
    position: "relative",
    zIndex: 2
  };

  const valueStyles = {
    color: theme.colors.textPrimary,
    fontSize: theme.fonts.sizes.xxl,
    fontWeight: theme.fonts.weights.bold,
    margin: "8px 0 4px 0",
    position: "relative",
    zIndex: 2
  };

  const labelStyles = {
    color: theme.colors.textSecondary,
    fontSize: theme.fonts.sizes.sm,
    fontWeight: theme.fonts.weights.medium,
    margin: "0 0 8px 0",
    position: "relative",
    zIndex: 2
  };

  const subtitleStyles = {
    color: theme.colors.textMuted,
    fontSize: theme.fonts.sizes.xs,
    margin: "0 0 8px 0",
    position: "relative",
    zIndex: 2
  };

  const trendStyles = {
    color: trend && trend.startsWith('+') ? theme.colors.success : theme.colors.danger,
    fontSize: theme.fonts.sizes.sm,
    fontWeight: theme.fonts.weights.semibold,
    margin: 0,
    position: "relative",
    zIndex: 2
  };

  const backgroundAccentStyles = {
    position: "absolute",
    top: "-20px",
    right: "-20px",
    width: "80px",
    height: "80px",
    //background: `${color}10`,
    borderRadius: "50%",
    zIndex: 1
  };

  return (
    <div
      style={cardStyles}
      //onMouseEnter={() => setIsHovered(true)}
      //onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      {...props}
    >
      <div style={backgroundAccentStyles} />
      
      <div style={iconContainerStyles}>
        <Icon size={28} />
      </div>
      
      <p style={valueStyles}>{value}</p>
      <p style={labelStyles}>{label}</p>
      {subtitle && <p style={subtitleStyles}>{subtitle}</p>}
      {trend && <p style={trendStyles}>{trend}</p>}
    </div>
  );
};

// Reusable Button Component
export const Mo3tiButton = ({ 
  children, 
  variant = "primary", 
  size = "md", 
  icon: Icon, 
  disabled = false,
  theme,
  className = "",
  ...props 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const getVariantStyles = () => {
    switch (variant) {
      case "primary":
        return {
          background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark})`,
          color: "#ffffff",
          border: "none"
        };
      case "secondary":
        return {
          background: theme.colors.inputBackground,
          color: theme.colors.textPrimary,
          border: `1px solid ${theme.colors.border}`
        };
      case "ghost":
        return {
          background: "transparent",
          color: theme.colors.textSecondary,
          border: `1px solid ${theme.colors.borderLight}`
        };
      case "danger":
        return {
          background: `linear-gradient(135deg, ${theme.colors.danger}, #dc2626)`,
          color: "#ffffff",
          border: "none"
        };
      default:
        return {
          background: theme.colors.inputBackground,
          color: theme.colors.textPrimary,
          border: `1px solid ${theme.colors.border}`
        };
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case "sm":
        return {
          padding: "8px 16px",
          fontSize: theme.fonts.sizes.xs,
          gap: "6px"
        };
      case "lg":
        return {
          padding: "16px 32px",
          fontSize: theme.fonts.sizes.base,
          gap: "10px"
        };
      default:
        return {
          padding: "12px 24px",
          fontSize: theme.fonts.sizes.sm,
          gap: "8px"
        };
    }
  };

  const baseStyles = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "12px",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    fontWeight: theme.fonts.weights.semibold,
    textDecoration: "none",
    outline: "none",
    position: "relative",
    overflow: "hidden",
    transform: isPressed ? "scale(0.98)" : isHovered ? "translateY(-1px)" : "translateY(0)",
    opacity: disabled ? 0.6 : 1,
    ...getVariantStyles(),
    ...getSizeStyles()
  };

  const handleMouseEnter = () => {
    if (!disabled) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setIsPressed(false);
  };

  const handleMouseDown = () => {
    if (!disabled) {
      setIsPressed(true);
    }
  };

  const handleMouseUp = () => {
    setIsPressed(false);
  };

  return (
    <button
      style={baseStyles}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      disabled={disabled}
      className={`mo3ti-button mo3ti-button-${variant} mo3ti-button-${size} ${className}`}
      {...props}
    >
      {Icon && <Icon size={size === "sm" ? 14 : size === "lg" ? 20 : 16} />}
      {children}
    </button>
  );
};

// Reusable Info Item Component
export const Mo3tiInfoItem = ({ 
  icon: Icon, 
  label, 
  value, 
  type = "text", 
  isEditing = false,
  theme,
  onChange,
  ...props 
}) => {
  const [inputValue, setInputValue] = useState(value);

  const itemStyles = {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px 0",
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    transition: "all 0.2s ease"
  };

  const iconContainerStyles = {
    width: "44px",
    height: "44px",
    background: `${theme.colors.primary}20`,
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.colors.primary,
    flexShrink: 0
  };

  const contentStyles = {
    flex: 1,
    minWidth: 0
  };

  const labelStyles = {
    color: theme.colors.textMuted,
    fontSize: theme.fonts.sizes.sm,
    margin: "0 0 4px 0",
    fontWeight: theme.fonts.weights.medium
  };

  const valueStyles = {
    color: theme.colors.textPrimary,
    fontSize: theme.fonts.sizes.base,
    fontWeight: theme.fonts.weights.medium,
    margin: 0,
    wordBreak: "break-word"
  };

  const inputStyles = {
    background: theme.colors.inputBackground,
    border: `2px solid ${theme.colors.border}`,
    borderRadius: "8px",
    padding: "8px 12px",
    color: theme.colors.textPrimary,
    fontSize: theme.fonts.sizes.base,
    width: "100%",
    outline: "none",
    transition: "border-color 0.2s ease"
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };

  const handleInputFocus = (e) => {
    e.currentTarget.style.borderColor = theme.colors.primary;
  };

  const handleInputBlur = (e) => {
    e.currentTarget.style.borderColor = theme.colors.border;
  };

  return (
    <div style={itemStyles} {...props}>
      <div style={iconContainerStyles}>
        <Icon size={20} />
      </div>
      <div style={contentStyles}>
        <p style={labelStyles}>{label}</p>
        {isEditing ? (
          <input
            type={type}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            style={inputStyles}
          />
        ) : (
          <p style={valueStyles}>{value}</p>
        )}
      </div>
    </div>
  );
};

// Reusable Progress Bar Component
export const Mo3tiProgressBar = ({ 
  percentage, 
  color, 
  theme, 
  showLabel = true,
  size = "md",
  animated = true,
  ...props 
}) => {
  const getSizeStyles = () => {
    switch (size) {
      case "sm":
        return { height: "6px", borderRadius: "3px" };
      case "lg":
        return { height: "12px", borderRadius: "6px" };
      default:
        return { height: "8px", borderRadius: "4px" };
    }
  };

  const containerStyles = {
    width: "100%",
    background: theme.colors.borderLight,
    overflow: "hidden",
    position: "relative",
    ...getSizeStyles()
  };

  const fillStyles = {
    width: `${Math.min(Math.max(percentage, 0), 100)}%`,
    height: "100%",
    background: `linear-gradient(90deg, ${color}, ${color}CC)`,
    transition: animated ? "width 0.8s ease" : "none",
    position: "relative"
  };

  const labelStyles = {
    position: "absolute",
    top: "50%",
    right: "8px",
    transform: "translateY(-50%)",
    color: "#ffffff",
    fontSize: theme.fonts.sizes.xs,
    fontWeight: theme.fonts.weights.semibold,
    textShadow: "0 1px 2px rgba(0,0,0,0.5)"
  };

  return (
    <div style={containerStyles} {...props}>
      <div style={fillStyles}>
        {showLabel && percentage > 20 && (
          <span style={labelStyles}>{Math.round(percentage)}%</span>
        )}
      </div>
    </div>
  );
};

// Reusable Loading Spinner Component
export const Mo3tiLoadingSpinner = ({ 
  size = "md", 
  color, 
  theme,
  ...props 
}) => {
  const getSizeValue = () => {
    switch (size) {
      case "sm": return "20px";
      case "lg": return "48px";
      default: return "32px";
    }
  };

  const spinnerStyles = {
    width: getSizeValue(),
    height: getSizeValue(),
    border: `3px solid ${theme.colors.borderLight}`,
    borderTop: `3px solid ${color || theme.colors.primary}`,
    borderRadius: "50%",
    animation: "mo3ti-spin 1s linear infinite"
  };

  return (
    <>
      <div style={spinnerStyles} {...props} />
      <style>
        {`
          @keyframes mo3ti-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

// Reusable Modal Component
export const Mo3tiModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  theme,
  size = "md",
  ...props 
}) => {
  if (!isOpen) return null;

  const getSizeStyles = () => {
    switch (size) {
      case "sm":
        return { width: "90%", maxWidth: "400px" };
      case "lg":
        return { width: "90%", maxWidth: "800px" };
      default:
        return { width: "90%", maxWidth: "600px" };
    }
  };

  const overlayStyles = {
    position: "fixed",
    inset: "0",
    background: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(5px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px"
  };

  const modalStyles = {
    background: theme.colors.cardBackground,
    backdropFilter: "blur(25px)",
    border: `1px solid ${theme.colors.border}`,
    borderRadius: "20px",
    boxShadow: theme.shadows.card,
    position: "relative",
    ...getSizeStyles()
  };

  const headerStyles = {
    padding: "24px 24px 0 24px",
    borderBottom: `1px solid ${theme.colors.border}`,
    marginBottom: "24px"
  };

  const titleStyles = {
    color: theme.colors.textPrimary,
    fontSize: theme.fonts.sizes.xl,
    fontWeight: theme.fonts.weights.semibold,
    margin: "0 0 16px 0"
  };

  const contentStyles = {
    padding: "0 24px 24px 24px",
    maxHeight: "70vh",
    overflowY: "auto"
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={overlayStyles} onClick={handleOverlayClick} {...props}>
      <div style={modalStyles}>
        {title && (
          <div style={headerStyles}>
            <h2 style={titleStyles}>{title}</h2>
          </div>
        )}
        <div style={contentStyles}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Reusable Toast Notification Component
export const Mo3tiToast = ({ 
  type = "info", 
  message, 
  isVisible, 
  onClose, 
  theme,
  duration = 4000,
  ...props 
}) => {
  const [shouldRender, setShouldRender] = useState(isVisible);

  React.useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      const timer = setTimeout(() => {
        onClose && onClose();
      }, duration);
      return () => clearTimeout(timer);
    } else {
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!shouldRender) return null;

  const getTypeStyles = () => {
    switch (type) {
      case "success":
        return {
          background: `${theme.colors.success}20`,
          borderColor: theme.colors.success,
          color: theme.colors.success
        };
      case "error":
        return {
          background: `${theme.colors.danger}20`,
          borderColor: theme.colors.danger,
          color: theme.colors.danger
        };
      case "warning":
        return {
          background: `${theme.colors.warning}20`,
          borderColor: theme.colors.warning,
          color: theme.colors.warning
        };
      default:
        return {
          background: `${theme.colors.info}20`,
          borderColor: theme.colors.info,
          color: theme.colors.info
        };
    }
  };

  const toastStyles = {
    position: "fixed",
    top: "20px",
    right: "20px",
    padding: "16px 20px",
    borderRadius: "12px",
    border: `2px solid`,
    backdropFilter: "blur(25px)",
    boxShadow: theme.shadows.card,
    zIndex: 1001,
    transform: isVisible ? "translateX(0)" : "translateX(100%)",
    opacity: isVisible ? 1 : 0,
    transition: "all 0.3s ease",
    minWidth: "300px",
    maxWidth: "500px",
    ...getTypeStyles()
  };

  const messageStyles = {
    margin: 0,
    fontSize: theme.fonts.sizes.sm,
    fontWeight: theme.fonts.weights.medium
  };

  return (
    <div style={toastStyles} {...props}>
      <p style={messageStyles}>{message}</p>
    </div>
  );
};

export default {
  Mo3tiCard,
  Mo3tiStatCard,
  Mo3tiButton,
  Mo3tiInfoItem,
  Mo3tiProgressBar,
  Mo3tiLoadingSpinner,
  Mo3tiModal,
  Mo3tiToast
};
